import React, { useEffect, useState } from "react";
import api from "../lib/api";
import {
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from "recharts";
import { Coins, Anchor, Flame, Crown, Wrench } from "lucide-react";

const currency = (n) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const COLORS = {
    operative: "hsl(45 85% 68%)",  // gold — partner / operative share
    platform: "hsl(40 70% 50%)",   // mustard — platform cut
    director: "hsl(153 55% 55%)",  // emerald — director residual
    referral: "hsl(25 80% 60%)",   // amber — referral pool
};

/**
 * Residual Breakdown HUD — Director-only chart that surfaces the 60/30/10 split
 * across boarding, fuel, and Foundry deliveries in one cohesive view.
 *
 * Two panels:
 *  1) Yield distribution donut — share of total committed capital flowing to each role.
 *  2) Channel breakdown bar chart — boarding vs fuel vs foundry residuals stacked.
 */
export default function ResidualBreakdown() {
    const [overview, setOverview] = useState(null);
    const [foundry, setFoundry] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        Promise.all([
            api.get("/director/overview").then((r) => r.data),
            api.get("/foundry/ledger").then((r) => r.data),
        ])
            .then(([o, f]) => {
                setOverview(o);
                setFoundry(f);
            })
            .catch((e) => setErr(e.response?.data?.detail || "Could not load residual data"));
    }, []);

    if (err) {
        return (
            <div className="glass rounded-2xl p-6 border border-[hsl(350_60%_45%/0.3)] text-sm text-[hsl(350_80%_72%)]" data-testid="residual-error">
                {err}
            </div>
        );
    }

    if (!overview || !foundry) {
        return (
            <div className="glass rounded-2xl p-8 border border-[hsl(40_40%_25%/0.25)] text-center text-sm text-muted-foreground" data-testid="residual-loading">
                Calibrating residual breakdown…
            </div>
        );
    }

    // --- Yield distribution donut --------------------------------------
    // Sovereign Covenant 60/30/10 (Director directive 2026.02 · LAW · Strike #21)
    // Boarding payments now follow the same global covenant as Foundry strikes.
    const boarding = overview.boarding_revenue || 0;
    const referralPool = overview.total_referral_earnings || 0;
    const foundryStats = foundry.stats || { total_revenue: 0, operative_payout_total: 0, director_cut_total: 0, anchor_cut_total: 0 };

    // Boarding splits — 60/30/10 across Commander / Director / Anchor.
    const boardingOperative = boarding * 0.60;     // Commander · Partner
    const boardingPlatform = boarding * 0.30;      // Director · EIGOO INC
    const boardingDirector = boarding * 0.10;      // Anchor · CyberKids

    const totalOperative = boardingOperative + (foundryStats.operative_payout_total || 0);
    const totalPlatform = boardingPlatform + (foundryStats.director_cut_total || 0);     // EIGOO INC residual
    const totalDirector = boardingDirector + (foundryStats.anchor_cut_total || 0);        // CyberKids tithe
    const totalReferral = referralPool;

    const donutData = [
        { name: "Commander · Partner (60%)", value: Math.round(totalOperative), color: COLORS.operative },
        { name: "Director · EIGOO INC (30%)", value: Math.round(totalPlatform), color: COLORS.platform },
        { name: "Anchor · CyberKids (10%)", value: Math.round(totalDirector), color: COLORS.director },
        { name: "Referral Pool", value: Math.round(totalReferral), color: COLORS.referral },
    ].filter((d) => d.value > 0);

    const grandTotal = donutData.reduce((s, d) => s + d.value, 0);

    // --- Channel breakdown bar chart -----------------------------------
    const barData = [
        {
            channel: "Boarding",
            Commander: Math.round(boardingOperative),
            Director: Math.round(boardingPlatform),
            Anchor: Math.round(boardingDirector),
        },
        {
            channel: "Foundry",
            Commander: Math.round(foundryStats.operative_payout_total || 0),
            Director: Math.round(foundryStats.director_cut_total || 0),
            Anchor: Math.round(foundryStats.anchor_cut_total || 0),
        },
    ];

    return (
        <section className="glass rounded-2xl p-6 md:p-8 border border-[hsl(40_40%_25%/0.3)] mt-10" data-testid="residual-breakdown">
            <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <Coins size={16} className="text-[hsl(45_85%_68%)]" />
                        <span className="text-[10px] uppercase tracking-[0.4em] text-[hsl(45_85%_68%)] font-semibold">
                            Director HUD · Residual Breakdown
                        </span>
                    </div>
                    <h2 className="font-heading text-3xl tracking-tight">
                        How every dollar splits across the fleet.
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1 max-w-md">
                        The 60 / 30 / 10 Sovereign Covenant rendered live across boarding bonds and Foundry strikes.
                    </p>
                </div>
                <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Total residual</div>
                    <div className="font-heading text-3xl gold-gradient-text mt-1" data-testid="residual-total">
                        {currency(grandTotal)}
                    </div>
                </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* Donut */}
                <div className="rounded-xl border border-[hsl(40_40%_25%/0.2)] p-4">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3 flex items-center gap-2">
                        <Crown size={11} className="text-[hsl(45_85%_68%)]" /> Yield Distribution
                    </div>
                    {donutData.length === 0 ? (
                        <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">
                            No completed transactions yet.
                        </div>
                    ) : (
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={donutData}
                                        innerRadius={55}
                                        outerRadius={90}
                                        paddingAngle={2}
                                        dataKey="value"
                                        nameKey="name"
                                        stroke="none"
                                    >
                                        {donutData.map((d, i) => (
                                            <Cell key={i} fill={d.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        formatter={(v) => currency(v)}
                                        contentStyle={{
                                            background: "hsl(151 55% 9%)",
                                            border: "1px solid hsl(40 40% 25% / 0.4)",
                                            borderRadius: 6,
                                        }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                    <ul className="mt-3 space-y-1.5 text-xs">
                        {donutData.map((d) => (
                            <li key={d.name} className="flex items-center justify-between gap-3">
                                <span className="flex items-center gap-2">
                                    <span className="h-2 w-2 rounded-full" style={{ background: d.color }} />
                                    <span className="text-muted-foreground">{d.name}</span>
                                </span>
                                <span className="font-mono tabular-nums text-foreground/85">{currency(d.value)}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Channel bar chart */}
                <div className="rounded-xl border border-[hsl(40_40%_25%/0.2)] p-4">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3 flex items-center gap-2">
                        <Wrench size={11} className="text-[hsl(45_85%_68%)]" /> Channel Breakdown
                    </div>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={barData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(40 20% 30% / 0.2)" />
                                <XAxis dataKey="channel" stroke="hsl(40 20% 60%)" fontSize={11} />
                                <YAxis stroke="hsl(40 20% 60%)" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                                <Tooltip
                                    formatter={(v) => currency(v)}
                                    contentStyle={{
                                        background: "hsl(151 55% 9%)",
                                        border: "1px solid hsl(40 40% 25% / 0.4)",
                                        borderRadius: 6,
                                    }}
                                />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Bar dataKey="Commander" stackId="a" fill={COLORS.operative} />
                                <Bar dataKey="Director" stackId="a" fill={COLORS.platform} />
                                <Bar dataKey="Anchor" stackId="a" fill={COLORS.director} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-2 text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                            <Anchor size={11} className="text-[hsl(45_85%_68%)]" /> Boarding bonds
                        </div>
                        <div className="flex items-center gap-1.5">
                            <Flame size={11} className="text-[hsl(25_90%_60%)]" /> Foundry strikes
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
